import React, { useMemo } from 'react'
import { H3, Subheading, Separator, Button, InfoSheetPanel, Row, Overline, usePageWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import ZionForm, { useZionForm, FormAutosuggest, useWatch } from '@fs/zion-form'
import {
  getNestedFloatingElementsByType,
  getPersonName,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewDispatchContext, useFamilyReviewStateContext, STEPS } from '../../providers/FamilyReviewContext'
import { getAllPersons, getDateFieldValues, getPlaceFieldValues } from '../../utils/utils'
import { saveFloatingEventToPerson } from '../../utils/saveFunctions'
import Tip from '../../../../components/tip/Tip'
import { showEventsHighlights } from '../../utils/highlightUtils'

export default function AddFloatingEvent({
  addNewEventOverlay,
  addFloatingEventOverlay,
  currentTask,
  editEventOverlay,
  setSaveUpdateCalled,
}) {
  const h = useSlsHighlightsContext()
  const dispatch = useFamilyReviewDispatchContext()
  const saveChangesFlag = useFeatureFlag('rip_sharedImageViewer_saveChanges')
  const zionSlsDispatch = useSlsDispatchContext()
  const zionSLSState = useSlsStateContext()
  const state = useFamilyReviewStateContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      floatingEvent: { key: '', primaryText: '' },
      person: { key: '', primaryText: '' },
    },
    resolver: (values) => {
      const errors = {}
      methods.clearErrors()
      const { floatingEvent, person } = values
      if (!floatingEvent?.key)
        errors.floatingEvent = { type: 'required', message: t('FormValidation.notEmpty', 'Field cannot be empty.') }
      if (!person?.key)
        errors.person = { type: 'required', message: t('FormValidation.notEmpty', 'Field cannot be empty.') }
      return { values, errors }
    },
  })

  const [floatingEvent, person] = useWatch({
    name: ['floatingEvent', 'person'],
    control: methods.control,
  })

  const handleSubmit = async () => {
    await saveFloatingEventToPerson({
      eventId: floatingEvent.key,
      personId: person.key,
      saveChangesFlag,
      userId: state.userId,
      zionSLSState,
      zionSlsDispatch,
    })
    setSaveUpdateCalled(true)
    dispatch({ type: 'SET_SELECTED_EVENT_ID', value: floatingEvent.key })
  }

  const resetHighlights = () => {
    showEventsHighlights({
      h,
      currentTask,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      eventIds: [state.stepThree.eventCards.find((card) => !card.hasBeenReviewed)?.eventId], // set highlights to next event to be reviewed
      isSmallScreen,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
  }

  const resetState = () => {
    const nextEventCard = state.stepThree.eventCards.find((cardState) => !cardState.hasBeenReviewed)
    if (nextEventCard) {
      dispatch({
        type: 'SET_SELECTED_EVENT_ID',
        value: nextEventCard.eventId,
      })
    }
  }

  const closeAddFloatingEvent = () => {
    methods.reset()
    addFloatingEventOverlay.close()
  }

  const saveButtonClicked = async () => {
    await handleSubmit()
    closeAddFloatingEvent()
    editEventOverlay.open()
  }

  const handleEventNotOnList = () => {
    if (person?.key) dispatch({ type: 'SET_ADDED_FLOATING_EVENT_PERSON', value: person.key })
    closeAddFloatingEvent()
    addNewEventOverlay.open()
  }

  const cancelButtonButtonClicked = () => {
    closeAddFloatingEvent()
    resetHighlights()
    resetState()
  }

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    closeAddFloatingEvent()
    resetHighlights()
    resetState()
  }

  const personSuggestions = getAllPersons(currentTask).map((personElement) => ({
    key: personElement?.id,
    primaryText: getPersonName({ person: personElement, justOneName: true }),
  }))

  const floatingEventSuggestions = useMemo(() => {
    const currentTaskEvents = currentTask.subElements
      ?.filter((se) => se.elementType === 'PERSON')
      .flatMap((currentTaskPerson) => currentTaskPerson?.subElements?.filter((el) => el.elementType === 'EVENT'))
    const recordHasPrimaryAlready = Boolean(currentTaskEvents.find((event) => event.typeData.primaryOnly))
    return getNestedFloatingElementsByType(zionSLSState.images[0], 'EVENT')
      .filter((event) => {
        if (recordHasPrimaryAlready) return !event.typeData.primaryOnly
        return event
      })
      .map((event) => {
        const dateValues = getDateFieldValues(event)
        const dateText = dateValues.map((date) => date.value).join(' ')
        const placeValues = getPlaceFieldValues(event)
        const placeText = placeValues.map((place) => place.value).join(' ')
        const secondaryText = dateText && placeText ? `${dateText} | ${placeText}` : dateText || placeText || ''
        return {
          key: event?.id,
          primaryText: event.typeData?.displayLabel || 'type',
          secondaryText,
        }
      })
  }, [zionSLSState.images, currentTask.subElements])

  const getTitle = () => {
    if (state.currentStep === STEPS.STEP_THREE) return t('family-review.step4.heading', 'Life Events')
    return t('family-review.step5.heading', 'Final Review')
  }

  return (
    <InfoSheetPanel
      {...{ ...addFloatingEventOverlay, close: userClosedPanel }}
      title={getTitle()}
      headingColor="green"
      footer={
        <Row alignX="end">
          <Button
            onClick={cancelButtonButtonClicked}
            linkName="Floating Event Cancel"
            data-testid="add-floating-event-cancel-button"
          >
            {t('edit-index.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            linkName="Floating Event Save"
            data-testid="add-floating-event-save-button"
            form="add-floating-event"
            type="submit"
          >
            {t('edit-index.save.button', 'Save')}
          </Button>
        </Row>
      }
    >
      {addFloatingEventOverlay.isOpen && (
        <>
          <H3 size="H5">
            <Overline>
              {t('family-review.stepXof4', 'Step {step} of 4', {
                step: state.currentStep === STEPS.STEP_THREE ? '3' : '4',
              })}
            </Overline>
            {t('family-review.addFloatingEvent.heading', 'Add an Event')}
            <Subheading>
              {t(
                'family-review.addFloatingEvent.content',
                'Select an event from the dropdown to add an existing event to the record.'
              )}
            </Subheading>
          </H3>
          <Separator />
          <ZionForm methods={methods} onSubmit={saveButtonClicked} id="add-floating-event">
            <FormAutosuggest
              data-testid="person-autosuggest"
              clearOnClickAway
              clearable
              id="event-person"
              name="person"
              label={t('family-review.addEvent.formLabel.person', 'Event Person')}
              placeholder={t('family-review.addEvent.formLabel.person', 'Event Person')}
              suggestions={personSuggestions}
            />
            <FormAutosuggest
              data-testid="floating-event-autosuggest"
              clearOnClickAway
              clearable
              id="floating-event"
              name="floatingEvent"
              label={t('edit-events.fields.new-event-heading', 'Event')}
              placeholder={t('edit-events.fields.new-event-heading', 'Event')}
              suggestions={floatingEventSuggestions}
            />
            <Button
              onClick={handleEventNotOnList}
              data-testid="event-not-on-list"
              linkName="Floating Event Not On List"
            >
              {t('family-review.addFloatingEvent.button', 'Event not on list')}
            </Button>
            <Tip
              primaryText={t('family-review.addFloatingEvent.tip.heading', 'What is an event?')}
              listPrimaryText={
                <>
                  {t(
                    'family-review.addFloatingEvent.tip.content',
                    'Events include births, deaths, marriages, burials, etc.'
                  )}
                </>
              }
            />
          </ZionForm>
        </>
      )}
    </InfoSheetPanel>
  )
}
